// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - CONTRACT CONSTANTS (Updated with Coin Type)
// Deployed: January 13, 2026
// ═══════════════════════════════════════════════════════════════════════════

export const SUI_NETWORK = 'mainnet';

// Main Package ID
export const DISTIL_PACKAGE_ID = '0xdad7e1e6f248a97f927e4be319658d12f9fb4efb522e43e91095f7ae58d59003';

// ═══════════════════════════════════════════════════════════════════════════
// COIN TYPE (For balance queries)
// Format: PackageID::ModuleName::StructName
// ═══════════════════════════════════════════════════════════════════════════
export const DISTIL_COIN_TYPE = `${DISTIL_PACKAGE_ID}::distil_coin::DISTIL_COIN`;

// Alternative format (same result):
// export const DISTIL_COIN_TYPE = '0xdad7e1e6f248a97f927e4be319658d12f9fb4efb522e43e91095f7ae58d59003::distil_coin::DISTIL_COIN';

// Distil Coin Module Objects
export const DISTIL_TREASURY_CAP_ID = '0x0b4c654ccbd33a52b55325c93d15d1d465203065aa22c50aefa846a23e1bc0f0'; // FROZEN - Immutable
export const DISTIL_COIN_METADATA_ID = '0x206f7e944b1860df45803ec556005e7bd2313315ddd6c9d68d1440640852ebdc';
export const DISTIL_COIN_OBJECT_ID = '0x4e4710cab3348d8f7fa238d6c949b96523e302481421800a1eaec145772312a6'; // Your 100M coin

// Refinery Module Objects (Shared - Public Access)
export const DISTIL_REFINERY_ID = '0xcba470e58b051e4e1dd5272f48ce1cba23127b1835aa46bf2444a8884a30e74e';
export const DISTIL_REFINERY_CONFIG_ID = '0xda0bcfd1672770dc350a7b9d068ac060bba62d867432dc1204bed9d8eabad6bd';
export const DISTIL_ADMIN_CAP_ID = '0x5769d517dac47377495defb00282d06649adaff6fec30e86502f19fffbada286';

// Purity Badge Module Objects
export const DISTIL_BADGE_REGISTRY_ID = '0x3c45ae7858f6286ba81d4bf2650858a27c272d81fe19686fc830b184e308ee39'; // Shared
export const DISTIL_PUBLISHER_ID = '0xfac531546b6469dfc63e8582ab36788ede453717cf60fe551bf943bf64e78e53';
export const DISTIL_DISPLAY_ID = '0x371a559bb3568c7706eca4fe185b5829f3b5dbc80a01ab19963d7aba1598ccff';
export const DISTIL_TRANSFER_POLICY_ID = '0xf5d9e0b711e4c02ba7664e5d3d226167b3ba13ca5e8a7ab32bdbceda07f59b5f'; // Shared
export const DISTIL_TRANSFER_POLICY_CAP_ID = '0x5fdbe6fa32a2a512025a1a3abd71969eeae294f592ea9538c4fbe2f3a4674615';

// Additional Objects
export const DISTIL_UPGRADE_CAP_ID = '0xedf6795fec33a7f6638919ae20b29f652e14328476b28a53c209a492bdfd2232';

// Fully Qualified Event Types
export const EVENT_TYPES = {
  // Refinery Events
  PURIFICATION: `${DISTIL_PACKAGE_ID}::refinery::PurificationEvent`,
  OPERATING_FUND_ALLOCATION: `${DISTIL_PACKAGE_ID}::refinery::OperatingFundAllocationEvent`,
  DISTILLATION: `${DISTIL_PACKAGE_ID}::refinery::DistillationEvent`,
  BUYBACK_EXECUTED: `${DISTIL_PACKAGE_ID}::refinery::BuybackExecutedEvent`,
  ADMIN_WITHDRAWAL: `${DISTIL_PACKAGE_ID}::refinery::AdminWithdrawalEvent`,
  
  // Purity Badge Events
  BADGE_MINTED: `${DISTIL_PACKAGE_ID}::purity_badge::BadgeMintedEvent`,
  BADGE_UPDATED: `${DISTIL_PACKAGE_ID}::purity_badge::BadgeUpdatedEvent`,
  TIER_UP: `${DISTIL_PACKAGE_ID}::purity_badge::TierUpEvent`,
  SPECIAL_SKIN_ACTIVATED: `${DISTIL_PACKAGE_ID}::purity_badge::SpecialSkinActivatedEvent`,
  SPECIAL_SKIN_DEACTIVATED: `${DISTIL_PACKAGE_ID}::purity_badge::SpecialSkinDeactivatedEvent`,
  
  // Distil Coin Events
  SUPPLY_FROZEN: `${DISTIL_PACKAGE_ID}::distil_coin::SupplyFrozenEvent`,
};

// Module Names
export const MODULES = {
  DISTIL_COIN: 'distil_coin',
  REFINERY: 'refinery',
  PURITY_BADGE: 'purity_badge',
};

// ═══════════════════════════════════════════════════════════════════════════
// BADGE TIERS - 8-tier system (UI progression)
// NOTE: On-chain contract has 4 tiers (0-3). These 8 UI tiers give users
//       more frequent progression milestones. When contract is upgraded to
//       support 8 tiers, these values map directly.
// Matches badgeManager.js BADGE_TIER_DATA exactly - single source of truth.
// ═══════════════════════════════════════════════════════════════════════════
export const BADGE_TIERS = {
  0: { name: 'Novice',     threshold: 0,     maxScore: 49,    color: '#9ca3af', emoji: '🌱', description: 'Just getting started' },
  1: { name: 'Apprentice', threshold: 50,    maxScore: 199,   color: '#60a5fa', emoji: '⚙️', description: 'Learning the ropes' },
  2: { name: 'Adept',      threshold: 200,   maxScore: 499,   color: '#8b5cf6', emoji: '⚡', description: 'Getting the hang of it' },
  3: { name: 'Expert',     threshold: 500,   maxScore: 999,   color: '#22d3ee', emoji: '💎', description: 'Purification master' },
  4: { name: 'Master',     threshold: 1000,  maxScore: 2499,  color: '#a855f7', emoji: '👑', description: 'Elite purifier' },
  5: { name: 'Elite',      threshold: 2500,  maxScore: 4999,  color: '#f59e0b', emoji: '🔥', description: 'Top tier performance' },
  6: { name: 'Legendary',  threshold: 5000,  maxScore: 9999,  color: '#ef4444', emoji: '⚔️', description: 'Legendary status achieved' },
  7: { name: 'Mythic',     threshold: 10000, maxScore: Infinity, color: '#FFD700', emoji: '🌟', description: 'Absolute legend' },
};

// Special Skins
export const SPECIAL_SKINS = {
  PORCELAIN_PRISM: 1, // Requires $TOILET holding
};

// Fee Configuration (basis points)
export const FEE_CONFIG = {
  BUYBACK_BPS: 9200, // 92%
  ADMIN_BPS: 800, // 8%
  TOTAL_BPS: 10000,
};

// ═══════════════════════════════════════════════════════════════════════════
// REWARD SPLIT - DEFLATIONARY TOKENOMICS (Task 1)
// 92% to user, 7% to protocol, 1% BURNED FOREVER
// ═══════════════════════════════════════════════════════════════════════════

export const REWARD_SPLIT = {
  USER_PERCENTAGE: 0.92,      // 92% to user
  PROTOCOL_PERCENTAGE: 0.07,  // 7% to protocol operations (reduced from 8%)
  BURN_PERCENTAGE: 0.01,      // 1% burned permanently (NEW!)
};

// Burn address (all burns go here - unrecoverable)
export const BURN_ADDRESS = '0x0000000000000000000000000000000000000000000000000000000000000000';

// Vault address - NFTs sent here for safe quarantine (recoverable by Steven)
export const DISTIL_VAULT_ADDRESS = '0x6c8677d4bfd76504db9ee630ccdcb3bce82b4fe6bc7b69470ebe12aee94a58fe';

// Badge Artwork URLs (update these when you upload to CDN)
export const BADGE_ARTWORK_BASE_URL = 'https://distil.protocol/badges';

export const getBadgeImageUrl = (tier, hasSpecialSkin = false) => {
  if (hasSpecialSkin) {
    return `${BADGE_ARTWORK_BASE_URL}/special/porcelain-prism-${tier}.png`;
  }
  return `${BADGE_ARTWORK_BASE_URL}/${tier}.png`;
};

// RPC Endpoints
export const RPC_ENDPOINTS = {
  testnet: 'https://fullnode.testnet.sui.io:443',
  mainnet: 'https://fullnode.mainnet.sui.io:443',
  devnet: 'https://fullnode.devnet.sui.io:443',
};

export const ACTIVE_RPC = RPC_ENDPOINTS[SUI_NETWORK];

// Explorer URLs
export const EXPLORER_URLS = {
  testnet: 'https://suiscan.xyz/testnet',
  mainnet: 'https://suiscan.xyz/mainnet',
  devnet: 'https://suiscan.xyz/devnet',
};

export const getExplorerUrl = (type, id) => {
  const base = EXPLORER_URLS[SUI_NETWORK];
  return `${base}/${type}/${id}`;
};

// Utility Functions
export const formatPurityScore = (score) => {
  return score.toLocaleString('en-US');
};

export const formatSuiAmount = (amount, decimals = 9) => {
  return (Number(amount) / Math.pow(10, decimals)).toFixed(4);
};

export const formatDistilBalance = (balanceString) => {
  // Parse balance and format with commas
  const [integer, decimal] = balanceString.split('.');
  const formattedInteger = Number(integer).toLocaleString('en-US');
  return decimal ? `${formattedInteger}.${decimal}` : formattedInteger;
};

// getTierFromScore - matches BADGE_TIERS 8-tier system above
// Returns tier index 0-7
export const getTierFromScore = (score) => {
  if (score >= 10000) return 7; // Mythic
  if (score >= 5000)  return 6; // Legendary
  if (score >= 2500)  return 5; // Elite
  if (score >= 1000)  return 4; // Master
  if (score >= 500)   return 3; // Expert
  if (score >= 200)   return 2; // Adept
  if (score >= 50)    return 1; // Apprentice
  return 0;                     // Novice
};

// Check if balance is high (1M+ for celebration animation)
export const isHighBalance = (balanceString) => {
  const numericBalance = parseFloat(balanceString.replace(/,/g, ''));
  return numericBalance >= 1000000;
};

// ═══════════════════════════════════════════════════════════════════════════
// BETA LAUNCH CONFIGURATION
// Controls when beta rewards/multipliers are visible to users
// ═══════════════════════════════════════════════════════════════════════════

export const BETA_LAUNCH_CONFIG = {
  // Set to true when ready to reveal beta program to users
  isLaunched: false, // ← FLIP TO TRUE ON MONDAY!
  
  // Current beta phase
  phase: 'private_beta', // 'private_beta' or 'public_beta'
  
  // Launch date (for display)
  startDate: '2026-02-10', // Monday launch
  
  // Phase multipliers
  multipliers: {
    private_beta: 2.0,  // Whale beta - 2x rewards
    public_beta: 1.5,   // Public beta - 1.5x rewards
  },
  
  // Base points per object deleted
  basePointsPerObject: 10,
};

// Get current multiplier based on phase
export const getCurrentMultiplier = () => {
  if (!BETA_LAUNCH_CONFIG.isLaunched) return 1.0;
  return BETA_LAUNCH_CONFIG.multipliers[BETA_LAUNCH_CONFIG.phase] || 1.0;
};

// Calculate points with multiplier
export const calculatePoints = (objectCount) => {
  const basePoints = objectCount * BETA_LAUNCH_CONFIG.basePointsPerObject;
  const multiplier = getCurrentMultiplier();
  return Math.floor(basePoints * multiplier);
};
